import { BlockedRequestError, fetchEligiblePlan } from "./Embedded/eligibility";
import { replaceWithIframe } from "./Embedded/fallback";
import { loadFont, waitForFont } from "./Embedded/font";
import {
  applyWidth,
  renderError,
  renderPaymentPlan,
  renderSkeleton,
} from "./Embedded/render";
import { elementNotFound, getElement } from "./helpers";
import { InitializeOptions } from "./types";

export function mount(options: InitializeOptions) {
  const {
    environment,
    merchantId,
    selector,
    amountInCents,
    installmentsCount,
    deferredDays,
    deferredMonths,
    locale,
    captureMethod,
    style,
    onIntegratedPayButtonClicked,
  } = options;
  if (!selector) {
    elementNotFound();
    return false;
  }

  // Merchants may pass strings (e.g. data attributes): parsed like the former checkout iframe did
  const toInteger = (value: unknown) => parseInt(String(value), 10);
  const parsedInstallmentsCount = toInteger(installmentsCount);

  const host = document.createElement("div");
  host.id = `alma-embedded-${selector.replace("#", "")}`;
  // `flow-root` keeps the margins of the payment plan on its background, `none` hides for credit
  host.style.width = "100%";
  host.style.display =
    parsedInstallmentsCount > 4 && !onIntegratedPayButtonClicked
      ? "none"
      : "flow-root";
  const root = host.attachShadow({ mode: "open" });
  getElement(selector)?.replaceChildren(host);

  const renderOptions = {
    locale,
    installmentsCount: parsedInstallmentsCount,
    isDeferredCapture: captureMethod === "manual",
    onPayButtonClicked: onIntegratedPayButtonClicked,
    containerStyle: style?.embedded,
    buttonStyle: style?.embeddedButton,
  };

  loadFont();
  renderSkeleton(root, renderOptions);

  const resizeObserver = new ResizeObserver(() => {
    if (host.isConnected) applyWidth(root, locale);
    else resizeObserver.disconnect();
  });
  resizeObserver.observe(host);

  fetchEligiblePlan(
    {
      merchantId,
      amountInCents: toInteger(amountInCents),
      installmentsCount: parsedInstallmentsCount,
      deferredDays: toInteger(deferredDays) || 0,
      deferredMonths: toInteger(deferredMonths) || 0,
      captureMethod: captureMethod ?? "automatic",
      locale: locale ?? "FR",
    },
    environment ?? "PROD"
  )
    .then(async (plan) => {
      await waitForFont();
      if (plan) {
        renderPaymentPlan(root, plan, renderOptions);
      } else {
        renderError(root, renderOptions);
      }
    })
    .catch((error) => {
      console.error("[Alma InPage] Failed to display the payment plan:", error);
      if (error instanceof BlockedRequestError) {
        replaceWithIframe(host, options);
      } else {
        renderError(root, renderOptions);
      }
    });

  return true;
}
